import InputError from '@/Components/InputError';
import InputLabel from '@/Components/InputLabel';
import PrimaryButton from '@/Components/PrimaryButton';
import TextInput from '@/Components/TextInput';
import {useConfirm} from '@/Components/ConfirmDialog';
import {Link, useForm, usePage} from '@inertiajs/react';
import {Transition} from '@headlessui/react';
import {FormEventHandler} from 'react';
import {PageProps} from '@/types';

export default function UpdateProfileInformation({mustVerifyEmail, hasPassword, status, className = '', onEmailChanging}: {
    mustVerifyEmail: boolean,
    hasPassword: boolean,
    status?: string,
    className?: string,
    onEmailChanging?: (isChanging: boolean) => void
}) {
    const p = usePage<PageProps>()
    const user = usePage<PageProps>().props.auth.user;
    const confirm = useConfirm();

    const {data, setData, patch, errors, processing, recentlySuccessful} = useForm({
        name: user.name,
        email: user.email,
        password: '',
        password_confirmation: '',
    });

    const submit: FormEventHandler = async (e) => {
        e.preventDefault();

        const emailChanged = data.email !== user.email;

        if (emailChanged) {
            const confirmed = await confirm({
                title: 'Change Email Address?',
                message: `You're about to change your email address to "${data.email}". This will require email verification before you can access all features. Are you sure you want to continue?`,
                confirmText: 'Yes, Change Email',
                cancelText: 'Cancel'
            });

            if (!confirmed) {
                return;
            }
        }

        patch(route('profile.update'));
    };

    return (
        <section className={className}>
            <header>
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Profile Information</h2>

                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Update your account's profile information and email address.
                </p>
            </header>

            <form onSubmit={submit} className="mt-6 space-y-6">
                <div>
                    <InputLabel htmlFor="name" value="Name"/>

                    <TextInput
                        id="name"
                        className="mt-1 block w-full"
                        value={data.name}
                        onChange={(e) => setData('name', e.target.value)}
                        required
                        isFocused
                        autoComplete="name"
                    />

                    <InputError className="mt-2" message={errors.name}/>
                </div>

                <div>
                    <InputLabel htmlFor="email" value="Email"/>

                    <TextInput
                        id="email"
                        type="email"
                        className={`mt-1 block w-full `}
                        value={data.email}
                        onChange={(e) => {
                            setData('email', e.target.value);
                            onEmailChanging?.(e.target.value !== user.email);
                        }}
                        required

                        autoComplete="username"
                    />

                    <InputError className="mt-2" message={errors.email}/>
                </div>

                {!hasPassword && data.email !== user.email && (
                    <>
                        <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-md">
                            <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                Changing your email will disconnect your social login. Please set a password so you can still sign in.
                            </p>
                        </div>

                        <div>
                            <InputLabel htmlFor="password" value="New Password"/>
                            <TextInput
                                id="password"
                                type="password"
                                className="mt-1 block w-full"
                                value={data.password}
                                onChange={(e) => setData('password', e.target.value)}
                                autoComplete="new-password"
                            />
                            <InputError className="mt-2" message={errors.password}/>
                        </div>

                        <div>
                            <InputLabel htmlFor="password_confirmation" value="Confirm Password"/>
                            <TextInput
                                id="password_confirmation"
                                type="password"
                                className="mt-1 block w-full"
                                value={data.password_confirmation}
                                onChange={(e) => setData('password_confirmation', e.target.value)}
                                autoComplete="new-password"
                            />
                            <InputError className="mt-2" message={errors.password_confirmation}/>
                        </div>
                    </>
                )}

                {mustVerifyEmail && user.email_verified_at === null && (
                    <div>
                        <p className="text-sm mt-2 text-gray-800 dark:text-gray-200">
                            Your email address is unverified.
                            <Link
                                href={route('verification.send')}
                                method="post"
                                as="button"
                                className="underline ml-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Click here to re-send the verification email.
                            </Link>
                        </p>

                        {status === 'verification-link-sent' && (
                            <div className="mt-2 font-medium text-sm text-green-600">
                                A new verification link has been sent to your email address.
                            </div>
                        )}
                    </div>
                )}

                {status === 'email-updated-verification-required' && (
                    <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-md">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            Your email address has been updated successfully. Please check your new email address and click the verification link to complete the process.
                        </p>
                    </div>
                )}

                <div className="flex items-center gap-4">
                    <PrimaryButton disabled={processing}>Save</PrimaryButton>

                    <Transition
                        show={recentlySuccessful}
                        enter="transition ease-in-out"
                        enterFrom="opacity-0"
                        leave="transition ease-in-out"
                        leaveTo="opacity-0"
                    >
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {status === 'profile-updated' ? 'Profile updated.' : 'Saved.'}
                        </p>
                    </Transition>
                </div>
            </form>
        </section>
    );
}
